import { useState, type ReactNode } from "react";
import useTitleBar from "../hooks/useTitleBar.tsx";
import type { View } from "../../../../common/types.ts";
import { helpers } from "../util/helpers.ts";
import { createMobileCrossTabHref } from "../util/mobileCrossTabLink.ts";
import i18n from "../util/i18n.ts";
import clsx from "clsx";
import { ContextFilter, FilterSheet } from "../components/ContextFilter.tsx";
import { EmptyState } from "../components/EmptyState.tsx";
import { NewsBlock } from "../components/NewsBlock.tsx";
import { categories, types } from "../util/transactionInfo.ts";
import {
  MobileContentHero,
  MobileExpertDisclosure,
} from "../mobile/mobilePrimitives.tsx";
import {
  getNewsHeadlineEvent,
  getNewsImpactKind,
  getNewsImportanceTier,
  getNewsRelevanceKind,
  groupNewsByPriority,
  groupNewsEventsBySeason,
} from "../util/newsRelevance.ts";
import { SafeHtml } from "../components/SafeHtml.tsx";
import { translateEventText } from "../util/eventText.ts";
import { translateTeamRegion } from "../util/teamNames.ts";

// B2c (v5Content) first screen: own-team latest 3 + league headline 1, on top
// of the top story. Everything else folds or paginates.
const NEWS_HERO_TEAM_LIMIT = 3;
// How many events each folded relevance segment renders; the rest stay
// reachable through the season timeline instead of being re-listed.
const NEWS_SEGMENT_LIMIT = 6;
// Per-section caps for the team-impact groups and the high-impact layer. Kept as
// constants so the render slice and the dedup claim stay in lock-step.
const NEWS_IMPACT_GROUP_LIMIT = 4;
// 3 keeps the layer inside the first-screen budget; overflow stays reachable
// through the season timeline (unclaimed events fall through).
const NEWS_HIGH_IMPACT_LIMIT = 3;
// Season-timeline pagination: primary events beyond this fold behind a lazy
// disclosure so the page stays within the height budget (V1 ≤4 screens).
const NEWS_TIMELINE_GROUP_LIMIT = 8;

// Closed <details> children still expose layout boxes (and entity links) to
// the audit, so folded news lists only mount their cards once opened (法则二).
const LazyNewsFold = ({
  children,
  className,
  summary,
  summaryClassName = "btn btn-link p-0",
  ...detailsProps
}: {
  children: ReactNode;
  className?: string;
  summary: ReactNode;
  summaryClassName?: string;
} & Record<string, unknown>) => {
  const [open, setOpen] = useState(false);

  return (
    <details
      className={className}
      data-native-details-host="news-lazy-fold"
      onToggle={(event) => {
        setOpen((event.currentTarget as HTMLDetailsElement).open);
      }}
      open={open}
      {...detailsProps}
    >
      <summary className={summaryClassName}>{summary}</summary>
      {open ? children : null}
    </details>
  );
};

const News = ({
  abbrev,
  currentSeason,
  events,
  level,
  order,
  season,
  teams,
  tid,
  userTid,
}: View<"news">) => {
  const [showCategories, setShowCategories] = useState<Record<keyof typeof categories, boolean>>({
    award: true,
    draft: true,
    injury: true,
    league: true,
    playerFeat: true,
    playoffs: true,
    rare: true,
    transaction: true,
    team: true,
  });

  const selectedTeam = tid !== undefined ? teams[tid] : undefined;
  const isTeamFiltered = abbrev !== "all" && selectedTeam !== undefined && tid !== undefined;
  const selectedTeamName = selectedTeam ? translateTeamRegion(selectedTeam.region) : undefined;
  const isHistoricalSeason = season < currentSeason;
  // R4a-1: the scope filters (team / season / importance) are the standard
  // title-bar scope chips (M5-2 dropdownFields) now — the same direct-manipulation
  // language every other data page uses — instead of the news-only "Adjust
  // content + Filtered: …" summary that duplicated them in the page body. A
  // customURL preserves the news route's positional order param (news/:abbrev/
  // :season/:level/:order) so deep-link / return semantics are unchanged; the
  // category (event-type) multi-select is not a single-value scope filter, so it
  // stays as the in-sheet control below. seasonsHistory-style forward picking now
  // works on mobile (the old body chips only offered reductive clear links).
  useTitleBar({
    title:
      isTeamFiltered && selectedTeamName
        ? isHistoricalSeason
          ? i18n.t("news.teamSeasonTitle", {
              ns: "ui",
              season,
              team: selectedTeamName,
              defaultValue: "{{team}} {{season}} news",
            })
          : i18n.t("news.teamTitle", {
              ns: "ui",
              team: selectedTeamName,
              defaultValue: "{{team}} news",
            })
        : i18n.t("news.title", { ns: "ui", defaultValue: "News Feed" }),
    dropdownView: "news",
    dropdownFields: {
      teamsAndAll: abbrev,
      seasons: season,
      newsLevels: level,
    },
    dropdownCustomURL: (fields) =>
      helpers.leagueUrl([
        "news",
        fields.teamsAndAll,
        fields.seasons,
        fields.newsLevels,
        order,
      ]),
  });
  const routeAbbrev = isTeamFiltered && selectedTeam ? `${selectedTeam.abbrev}_${tid}` : "all";
  const createNewsHref = ({
    nextAbbrev = routeAbbrev,
    nextLevel = level,
    nextOrder = order,
    nextSeason = season,
  }: {
    nextAbbrev?: string;
    nextLevel?: typeof level;
    nextOrder?: typeof order;
    nextSeason?: number;
  } = {}) => helpers.leagueUrl(["news", nextAbbrev, nextSeason, nextLevel, nextOrder]);
  const activeCategoryCount = helpers
    .keys(showCategories)
    .filter((category) => showCategories[category]).length;
  const visibleEvents = events.filter((event) => {
    const type = types[event.type];
    if (type) {
      return showCategories[type.category] === true;
    }
    return true;
  });
  const headlineEvent = getNewsHeadlineEvent(visibleEvents, userTid);
  const headlineEid = headlineEvent?.eid;
  // Mutually-exclusive priority split: one event belongs to exactly one of
  // 本队相关 > 联赛大事 > 其他, so it renders in a single feed segment (法则二).
  // The headline already shows one event, so keep it out of the segments.
  const priorityGroups = groupNewsByPriority(visibleEvents, userTid);
  const notHeadline = (event: (typeof visibleEvents)[number]) => event.eid !== headlineEid;
  const userTeamEvents = priorityGroups.userTeam.filter(notHeadline);
  const leagueMajorEvents = priorityGroups.leagueMajor.filter(notHeadline);
  const moreNewsEvents = priorityGroups.other.filter(notHeadline);
  // B2c first screen: own-team latest 3 + league headline 1 inside the hero;
  // the segment remainders fold below it.
  const heroTeamEvents = userTeamEvents.slice(0, NEWS_HERO_TEAM_LIMIT);
  const restTeamEvents = userTeamEvents.slice(NEWS_HERO_TEAM_LIMIT);
  const heroLeagueEvent = leagueMajorEvents[0];
  const restLeagueEvents = leagueMajorEvents.slice(1);
  const highImportanceEvents = visibleEvents.filter(
    (event) => getNewsImportanceTier(event) === "high",
  );
  // Events the hero + folded feed already render. Downstream layers (team
  // impact, high-impact, season timeline) exclude these so no event card — and
  // therefore no entity link — appears more than once on the page (法则二). The
  // folded segments only render the first NEWS_SEGMENT_LIMIT events, so the
  // overflow stays visible in the season timeline instead of being dropped.
  const claimedEids = new Set<number>();
  if (headlineEid !== undefined) {
    claimedEids.add(headlineEid);
  }
  for (const event of heroTeamEvents) {
    claimedEids.add(event.eid);
  }
  if (heroLeagueEvent) {
    claimedEids.add(heroLeagueEvent.eid);
  }
  for (const event of restTeamEvents.slice(0, NEWS_SEGMENT_LIMIT)) {
    claimedEids.add(event.eid);
  }
  for (const event of restLeagueEvents.slice(0, NEWS_SEGMENT_LIMIT)) {
    claimedEids.add(event.eid);
  }
  for (const event of moreNewsEvents) {
    claimedEids.add(event.eid);
  }
  const isUnclaimed = (event: (typeof visibleEvents)[number]) => !claimedEids.has(event.eid);
  const claimEvents = (claimable: typeof visibleEvents) => {
    for (const event of claimable) {
      claimedEids.add(event.eid);
    }
  };
  // Team-impact groups (only rendered when the feed is team-filtered) draw from
  // events the hero + folded feed have not already shown.
  const teamImpactEvents = (isTeamFiltered ? visibleEvents : userTeamEvents).filter(isUnclaimed);
  const teamRoutineEvents = teamImpactEvents.filter(
    (event) => getNewsImportanceTier(event) === "ordinary",
  );
  const teamImpactGroups: {
    attrs: Record<string, string>;
    events: typeof visibleEvents;
    title: string;
  }[] = [
    {
      attrs: {},
      events: teamImpactEvents.filter((event) => getNewsImpactKind(event) === "roster"),
      title: i18n.t("news.p1s08ImpactRosterTitle", {
        ns: "ui",
        defaultValue: "Roster",
      }),
    },
    {
      attrs: {},
      events: teamImpactEvents.filter((event) => getNewsImpactKind(event) === "market"),
      title: i18n.t("news.p1s08ImpactMarketTitle", {
        ns: "ui",
        defaultValue: "Market",
      }),
    },
    {
      attrs: {},
      events: teamImpactEvents.filter((event) => getNewsImpactKind(event) === "standings"),
      title: i18n.t("news.p1s08ImpactStandingsTitle", {
        ns: "ui",
        defaultValue: "Results",
      }),
    },
    {
      attrs: {},
      events: teamImpactEvents.filter((event) => getNewsImpactKind(event) === "star"),
      title: i18n.t("news.p1s08ImpactHonorsTitle", {
        ns: "ui",
        defaultValue: "Honors",
      }),
    },
  ];
  // Claim each section's rendered events in on-screen order (team impact → high
  // impact → season timeline) so a later section never repeats an event an
  // earlier one already showed.
  if (isTeamFiltered) {
    for (const group of teamImpactGroups) {
      claimEvents(group.events.slice(0, NEWS_IMPACT_GROUP_LIMIT));
    }
    claimEvents(teamRoutineEvents);
  }
  const highImpactLayerEvents = highImportanceEvents.filter(isUnclaimed);
  claimEvents(highImpactLayerEvents.slice(0, NEWS_HIGH_IMPACT_LIMIT));
  const groupedEvents = groupNewsEventsBySeason(visibleEvents.filter(isUnclaimed));
  const hasVisibleEvents = visibleEvents.length > 0;
  const sourceEventEids = events.map((event) => event.eid);
  const createNewsImpactActionHref = (event: (typeof visibleEvents)[number]) => {
    const eventTid = event.tid ?? event.tids?.[0] ?? tid ?? userTid;
    const eventTeam = eventTid !== undefined ? teams[eventTid] : undefined;
    const teamRouteParam = eventTeam ? `${eventTeam.abbrev}_${eventTid}` : undefined;
    const rosterHref = teamRouteParam
      ? helpers.leagueUrl(["roster", teamRouteParam, season])
      : helpers.leagueUrl(["roster"]);

    // N2-2: these impact handles jump out of the news (league) tab, so seed the
    // back stack with the current news page for a precise multi-level return.
    switch (getNewsImpactKind(event)) {
      case "draft":
        return createMobileCrossTabHref(helpers.leagueUrl(["draft"]));
      case "market":
        return createMobileCrossTabHref(helpers.leagueUrl(["free_agents"]));
      case "roster":
        return createMobileCrossTabHref(rosterHref);
      case "standings":
        return createMobileCrossTabHref(helpers.leagueUrl(["standings", season]));
      case "star":
        return createMobileCrossTabHref(
          event.pids?.[0] !== undefined ? helpers.leagueUrl(["player", event.pids[0]]) : rosterHref,
        );
      default:
        return createNewsHref({ nextLevel: "big" });
    }
  };
  const showAllCategories = () => {
    const show = { ...showCategories };
    for (const key of helpers.keys(show)) {
      show[key] = true;
    }
    setShowCategories(show);
  };
  const hideAllCategories = () => {
    const show = { ...showCategories };
    for (const key of helpers.keys(show)) {
      show[key] = false;
    }
    setShowCategories(show);
  };
  const renderNewsCards = (
    newsEvents: typeof visibleEvents,
    listAttrs: Record<string, number | string> = {},
  ) => (
    <div
      className="row"
      data-mobile-list-card-count={newsEvents.length}
      data-mobile-list-card-list=""
      role="list"
      {...listAttrs}
    >
      {newsEvents.map((event) => (
        <div key={event.eid} className="col-lg-3 col-md-4 col-sm-6 col-12 mb-3">
          <NewsBlock event={event} season={season} teams={teams} userTid={userTid} />
        </div>
      ))}
    </div>
  );
  // Folded relevance segment: summary carries the count; cards mount on open.
  const renderFoldedSegment = ({
    attrs,
    events: segmentEvents,
    summaryText,
  }: {
    attrs: Record<string, string>;
    events: typeof visibleEvents;
    summaryText: string;
  }) =>
    segmentEvents.length > 0 ? (
      <section className="mb-2" {...attrs}>
        <LazyNewsFold summary={summaryText}>
          {renderNewsCards(segmentEvents.slice(0, NEWS_SEGMENT_LIMIT), {})}
        </LazyNewsFold>
      </section>
    ) : null;
  const renderP1TeamImpactGroup = ({
    attrs,
    events: groupEvents,
    title,
  }: {
    attrs: Record<string, string>;
    events: typeof visibleEvents;
    title: string;
  }) =>
    groupEvents.length > 0 ? (
      <section className="mb-3" {...attrs}>
        <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
          <h3 className="h6 mb-0">{title}</h3>
          <span className="badge text-bg-light border">{groupEvents.length}</span>
        </div>
        {renderNewsCards(groupEvents.slice(0, NEWS_IMPACT_GROUP_LIMIT), {})}
      </section>
    ) : null;
  const renderCategoryControls = (idPrefix: string) => (
    <>
      <div className="mt-1" style={{ marginLeft: "-0.5rem" }}>
        {helpers.keys(categories).map((category) => {
          const info = categories[category];
          return (
            <div key={category} className={clsx("form-check form-check-inline mb-2 ms-2", {})}>
              <input
                className="form-check-input"
                type="checkbox"
                checked={showCategories[category]}
                data-news-category={category}
                id={`${idPrefix}-${category}`}
                onChange={() => {
                  setShowCategories((show) => ({
                    ...show,
                    [category]: !show[category],
                  }));
                }}
              />
              <label
                className={`form-check-label badge badge-news ${info.className}`}
                htmlFor={`${idPrefix}-${category}`}
              >
                {info.text}
              </label>
            </div>
          );
        })}
      </div>

      <div className="mb-3">
        <button
          className="btn btn-link p-0"
          data-news-category-action="all"
          onClick={(event) => {
            event.preventDefault();
            showAllCategories();
          }}
        >
          {i18n.t("common.all", { ns: "ui", defaultValue: "All" })}
        </button>{" "}
        |{" "}
        <button
          className="btn btn-link p-0"
          data-news-category-action="none"
          onClick={(event) => {
            event.preventDefault();
            hideAllCategories();
          }}
        >
          {i18n.t("common.none", { ns: "ui", defaultValue: "None" })}
        </button>
      </div>
    </>
  );

  // R4a-1: the scope filter state (team / season / importance) + its clear
  // routes moved to the title-bar scope chips (see useTitleBar above). The
  // page-body chip row that duplicated them is gone (删页内重复表达); the filter
  // sheet keeps only the category (event-type) multi-select, which is not a
  // single-value scope filter and so has no chip equivalent.
  const categoryTotal = helpers.keys(categories).length;

  return (
    <div
      data-host-state={hasVisibleEvents ? "ready" : "empty"}
      data-host-state-host="news"
      data-host-state-row-count={visibleEvents.length}
      data-news-order-current={order}
      data-news-source-event-count={sourceEventEids.length}
      data-news-source-event-eids={sourceEventEids.join(",")}
      data-v5-news-content-first-screen=""
    >
      {hasVisibleEvents ? (
        // B2c (v5Content): the first screen IS the news — top story, own-team
        // latest 3, league headline 1. The archive-hero narration card and the
        // insight strip are gone (法则一).
        <MobileContentHero
          className="mb-3"
          data-team-headline=""
          data-team-headline-state={
            headlineEvent ? getNewsRelevanceKind(headlineEvent, userTid) : "empty"
          }
          data-v5-news-team-first-screen=""
        >
          {headlineEvent ? (
            <div className="mobile-news-headline" data-mobile-news-headline-event="">
              <div className="mobile-news-headline-label">
                {i18n.t("news.mobileTopStory", {
                  ns: "ui",
                  defaultValue: "Top story",
                })}
              </div>
              <div className="mobile-news-headline-text" data-localized-event-text="">
                <SafeHtml dirty={translateEventText(headlineEvent)} />
              </div>
            </div>
          ) : null}
          {heroTeamEvents.length > 0
            ? renderNewsCards(heroTeamEvents, {
                "data-v5-news-team-latest": "",
              })
            : null}
          {heroLeagueEvent
            ? renderNewsCards([heroLeagueEvent], {
                "data-v5-news-league-headline": "",
              })
            : null}
        </MobileContentHero>
      ) : (
        <div data-mobile-news-empty-first="">
          <EmptyState
            action={
              <div className="d-flex flex-wrap gap-2">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={showAllCategories}
                  type="button"
                >
                  {i18n.t("news.resetCategoryFilters", {
                    ns: "ui",
                    defaultValue: "Show all categories",
                  })}
                </button>
                <a
                  className="btn btn-outline-secondary btn-sm"
                  href={createNewsHref({
                    nextAbbrev: "all",
                    nextLevel: "big",
                    nextOrder: "newest",
                    nextSeason: currentSeason,
                  })}
                >
                  {i18n.t("news.resetFilters", {
                    ns: "ui",
                    defaultValue: "Reset news filters",
                  })}
                </a>
              </div>
            }
            reason="filtered"
            title={i18n.t("news.emptyFilteredTitle", {
              ns: "ui",
              defaultValue: "No matching news",
            })}
          >
            {i18n.t("news.emptyFilteredBody", {
              ns: "ui",
              defaultValue:
                "Clear the team, season, importance, or category filters to return to the full feed.",
            })}
          </EmptyState>
        </div>
      )}

      <ContextFilter activeCount={hasVisibleEvents ? activeCategoryCount : undefined}>
        {/* One filter entry for the event-type categories; scope (team / season /
            importance) lives in the title-bar chips now, so the trigger label
            summarizes the category selection, not the scope. */}
        <div className="mb-3">
          <FilterSheet
            activeCount={hasVisibleEvents ? activeCategoryCount : undefined}
            id="news-filter-sheet"
            title={i18n.t("news.filters", {
              ns: "ui",
              defaultValue: "News filters",
            })}
            renderTarget={({ onClick }) => (
              <button
                className="btn btn-outline-secondary btn-sm w-100 d-flex justify-content-between align-items-center gap-2"
                data-mobile-news-filter-trigger=""
                onClick={onClick}
                type="button"
              >
                <span>
                  {i18n.t("news.p1s08AdjustContent", {
                    ns: "ui",
                    defaultValue: "Adjust content",
                  })}
                </span>
                <span className="small text-body-secondary text-truncate">
                  {i18n.t("news.filterCategorySummary", {
                    ns: "ui",
                    count: activeCategoryCount,
                    total: categoryTotal,
                    defaultValue: "{{count}}/{{total}} categories",
                  })}
                </span>
              </button>
            )}
          >
            {renderCategoryControls("news-mobile")}
          </FilterSheet>
        </div>
      </ContextFilter>

      <nav
        aria-label={i18n.t("news.timelineOrderLabel", {
          ns: "ui",
          defaultValue: "Timeline order",
        })}
        className="d-flex align-items-center justify-content-between gap-2 mb-3"
        data-news-timeline-order=""
      >
        <span className="small fw-semibold">
          {i18n.t("news.timelineOrderLabel", {
            ns: "ui",
            defaultValue: "Timeline order",
          })}
        </span>
        <div className="btn-group btn-group-sm" role="group">
          {(["newest", "oldest"] as const).map((nextOrder) => (
            <a
              aria-current={order === nextOrder ? "page" : undefined}
              className={clsx("btn", {
                "btn-outline-secondary": order !== nextOrder,
                "btn-primary": order === nextOrder,
              })}
              data-news-order-option={nextOrder}
              href={createNewsHref({ nextOrder })}
              key={nextOrder}
            >
              {nextOrder === "newest"
                ? i18n.t("dropdown.newest", {
                    ns: "ui",
                    defaultValue: "Newest First",
                  })
                : i18n.t("dropdown.oldest", {
                    ns: "ui",
                    defaultValue: "Oldest First",
                  })}
            </a>
          ))}
        </div>
      </nav>

      {isTeamFiltered && selectedTeam ? (
        // Team/archive links remain available with exact same-feed or cross-tab
        // return state, but stay folded behind the news conclusion by default.
        <MobileExpertDisclosure
          className="mb-3"
          data-mobile-news-team-links=""
          expertHostId="news-team-and-archive-links"
          summary={i18n.t("news.teamLinksSummary", {
            ns: "ui",
            defaultValue: "Team and archive links",
          })}
        >
          <div className="d-flex flex-wrap gap-2">
            <a
              className="btn btn-outline-secondary btn-sm"
              href={createMobileCrossTabHref(
                helpers.leagueUrl(["roster", `${selectedTeam.abbrev}_${tid}`, season]),
              )}
            >
              {i18n.t("news.returnTeamHome", {
                ns: "ui",
                team: selectedTeamName,
                defaultValue: "Return to {{team}} home",
              })}
            </a>
            {isHistoricalSeason ? (
              <>
                <a
                  className="btn btn-outline-secondary btn-sm"
                  data-news-return-current=""
                  href={createNewsHref({ nextSeason: currentSeason })}
                >
                  {i18n.t("news.returnCurrentNews", {
                    ns: "ui",
                    defaultValue: "Back to current news",
                  })}
                </a>
                <a
                  className="btn btn-outline-secondary btn-sm"
                  href={createMobileCrossTabHref(
                    helpers.leagueUrl(["roster", `${selectedTeam.abbrev}_${tid}`]),
                  )}
                >
                  {i18n.t("news.openTeamHistory", {
                    ns: "ui",
                    defaultValue: "Team history",
                  })}
                </a>
              </>
            ) : null}
          </div>
        </MobileExpertDisclosure>
      ) : null}

      <section className="mb-3">
        {renderFoldedSegment({
          attrs: { "data-v5-news-more-team-fold": "" },
          events: restTeamEvents,
          summaryText: i18n.t("news.moreTeamNews", {
            ns: "ui",
            count: restTeamEvents.length,
            defaultValue: "More team updates ({{count}})",
          }),
        })}
        {renderFoldedSegment({
          attrs: {},
          events: restLeagueEvents,
          summaryText: i18n.t("news.moreLeagueNews", {
            ns: "ui",
            count: restLeagueEvents.length,
            defaultValue: "More league stories ({{count}})",
          }),
        })}
        {moreNewsEvents.length > 0 ? (
          <LazyNewsFold
            className="mb-3"
            summary={i18n.t("news.p1s08MoreNewsSection", {
              ns: "ui",
              count: moreNewsEvents.length,
              defaultValue: "More news ({{count}})",
            })}
          >
            {renderNewsCards(moreNewsEvents, {})}
          </LazyNewsFold>
        ) : (
          <p className="small text-body-secondary mb-0 d-none">
            {i18n.t("news.p1s08SegmentEmpty", {
              ns: "ui",
              defaultValue: "No updates here yet.",
            })}
          </p>
        )}
      </section>

      {isTeamFiltered ? (
        <section className="mb-3">
          <h2 className="h5 mb-2">
            {i18n.t("news.p1s08TeamImpactTitle", {
              ns: "ui",
              defaultValue: "News that affects this team",
            })}
          </h2>
          {teamImpactGroups.map((group) => (
            <div key={group.title}>{renderP1TeamImpactGroup(group)}</div>
          ))}
          {teamRoutineEvents.length > 0 ? (
            <LazyNewsFold
              className="mb-3"
              summary={i18n.t("news.p1s08TeamOrdinarySummary", {
                ns: "ui",
                count: teamRoutineEvents.length,
                defaultValue: "Ordinary team updates ({{count}})",
              })}
            >
              {renderNewsCards(teamRoutineEvents, {})}
            </LazyNewsFold>
          ) : null}
        </section>
      ) : null}

      {isTeamFiltered && isHistoricalSeason ? (
        <section className="alert alert-light border py-2" data-news-archive-timeline="">
          <div className="fw-semibold">
            {i18n.t("news.p1s08HistoryTimelineTitle", {
              ns: "ui",
              season,
              defaultValue: "{{season}} team timeline",
            })}
          </div>
        </section>
      ) : null}

      {highImpactLayerEvents.length > 0 ? (
        <section className="mb-3">
          <h2 className="h5 mb-1">
            {i18n.t("news.p1s08HighImpactTitle", {
              ns: "ui",
              defaultValue: "High-impact events",
            })}
          </h2>
          <div className="d-grid gap-2" role="list">
            {highImpactLayerEvents.slice(0, NEWS_HIGH_IMPACT_LIMIT).map((event) => {
              const eventHtml = translateEventText(event);
              const handleHref = createNewsImpactActionHref(event);
              // 法则二: when the event text already links the same object, the
              // inline link IS the handle — a second CTA to the same href
              // would duplicate the entity link.
              const handleAlreadyInText = eventHtml.includes(`"${handleHref}"`);

              return (
                <article className="border rounded p-2" key={event.eid} role="listitem">
                  <div data-localized-event-text="">
                    <SafeHtml dirty={eventHtml} />
                  </div>
                  {handleAlreadyInText ? null : (
                    <a className="btn btn-outline-primary btn-sm mt-2" href={handleHref}>
                      {i18n.t("news.p1s08HandleAction", {
                        ns: "ui",
                        defaultValue: "View",
                      })}
                    </a>
                  )}
                </article>
              );
            })}
          </div>
        </section>
      ) : null}

      {groupedEvents.map((group) => {
        const primaryEvents = group.events.filter(
          (event) => getNewsImportanceTier(event) !== "ordinary",
        );
        const routineEvents = group.events.filter(
          (event) => getNewsImportanceTier(event) === "ordinary",
        );
        const eventsToRender = primaryEvents.length > 0 ? primaryEvents : group.events;
        const collapsedRoutineEvents = primaryEvents.length > 0 ? routineEvents : [];
        // Pagination (V1 height budget): the first NEWS_TIMELINE_GROUP_LIMIT
        // primary events render directly; the rest fold behind a lazy
        // disclosure instead of stacking more screens.
        const pagedEvents = eventsToRender.slice(0, NEWS_TIMELINE_GROUP_LIMIT);
        const overflowEvents = eventsToRender.slice(NEWS_TIMELINE_GROUP_LIMIT);

        return (
          <section data-date-groups="" key={group.season}>
            <h2>
              {group.season > 0
                ? i18n.t("news.mobileSeasonGroup", {
                    ns: "ui",
                    defaultValue: "{{season}} news",
                    season: group.season,
                  })
                : i18n.t("news.mobileUndatedGroup", {
                    ns: "ui",
                    defaultValue: "League news",
                  })}
            </h2>
            {renderNewsCards(pagedEvents, {})}
            {overflowEvents.length > 0 ? (
              <LazyNewsFold
                className="mb-3"
                data-v5-news-timeline-overflow=""
                summary={i18n.t("news.showMoreSeasonNews", {
                  ns: "ui",
                  count: overflowEvents.length,
                  defaultValue: "Show {{count}} more updates",
                })}
              >
                {renderNewsCards(overflowEvents)}
              </LazyNewsFold>
            ) : null}
            {collapsedRoutineEvents.length > 0 ? (
              <LazyNewsFold
                className="mb-3"
                summary={i18n.t("news.routineUpdatesSummary", {
                  ns: "ui",
                  count: collapsedRoutineEvents.length,
                  defaultValue: "Show {{count}} routine updates",
                })}
              >
                <div>{renderNewsCards(collapsedRoutineEvents, {})}</div>
              </LazyNewsFold>
            ) : null}
          </section>
        );
      })}
    </div>
  );
};

export default News;
